import React, {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { RomaneioFornContextData } from '../types/context';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import {
  DisabledProps,
  FormType,
  productProps,
  selectProps,
  selectTipoAjusteProps,
} from '../types';
import { defaultData, defaultDisabled, productDefault } from '../DefaultData';
import { GridRowParams } from '@material-ui/data-grid';
import { LojaContext } from '~/context/loja';
import { schema } from '../Validations';
import { romaneioServices } from '../services';
import {
  formatCurrencyAsText,
  moneyFormat,
  transformAsCurrency,
} from '~/utils/functions';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { toast } from 'react-toastify';
import { toleraValor } from '../utils/ToleraValor';
import { calculaQtdEstPost } from '../utils/calcQtdEstPost';

export const RomaneioFornContext = createContext({} as RomaneioFornContextData);

const MySwal = withReactContent(Swal);

export const RomaneioFornProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { codLoja, loja } = useContext(LojaContext);
  const [showSearch, setShowSearch] = useState<boolean>(true);
  const [isUpdate, setUpdate] = useState<boolean>(false);
  const [flgCancelado, setFlgCancelado] = useState<boolean>(false);
  const [productSelected, setProductSelected] =
    useState<productProps>(productDefault);
  const [lojaSelected, setLojaSelected] = useState<number>(codLoja);
  const [tiposAjuste, setTiposAjuste] = useState<selectTipoAjusteProps[]>([]);
  const [unidades, setUnidades] = useState<selectProps[]>([]);
  const [disabledInputs, setDisabledInputs] =
    useState<DisabledProps>(defaultDisabled);

  const {
    register,
    getValues,
    handleSubmit,
    formState,
    control,
    setValue,
    watch,
    reset,
    setFocus,
    clearErrors,
    setError,
    resetField,
  } = useForm({
    resolver: yupResolver(schema),
    reValidateMode: 'onChange',
  });

  const formTela: FormType = useMemo(
    () => ({
      register,
      getValues,
      handleSubmit,
      formState,
      control,
      setValue,
      watch,
      reset,
      setFocus,
      clearErrors,
      setError,
      resetField,
    }),
    [
      register,
      getValues,
      handleSubmit,
      formState,
      control,
      setValue,
      watch,
      reset,
      setFocus,
      clearErrors,
      setError,
      resetField,
    ],
  );

  const handleFocus = useCallback((campo: string) => {
    if (!campo) return;

    const input =
      document.getElementById(campo) ||
      (document.getElementsByName(campo)[0] as HTMLElement);

    if (input) {
      setTimeout(() => {
        input.focus();
        if (typeof input.click === 'function') input.click();
      }, 60);
    }
  }, []);

  const refetchUnidades = useCallback(async () => {
    const { data } = await romaneioServices.getUnidades();
    const options = data[1].map((unidade: any) => {
      return {
        value: unidade.des_unidade,
        label: `${unidade.des_unidade} - ${unidade.des_medida}`,
      };
    });
    setUnidades(options);
  }, []);

  const refetchTiposAjuste = useCallback(async () => {
    const { data } = await romaneioServices.getAjustes();
    const options = data.map((i: any) => {
      return {
        value: i.cod_tipo_ajuste,
        label: `${i.cod_tipo_ajuste} - ${i.des_tipo_operacao} ${i.des_ajuste}`,
        tipo_unidade: i.tipo_unidade,
        tipo_operacao: i.tipo_operacao,
      };
    });
    setTiposAjuste(options);
  }, []);

  useEffect(() => {
    refetchUnidades();
  }, [refetchUnidades, isUpdate]);

  const handleLojaSelected = useCallback((cod_loja: number) => {
    setLojaSelected(cod_loja);
  }, []);

  const resetFormData = useCallback(() => {
    reset(defaultData);
    setProductSelected(productDefault);
    setDisabledInputs(defaultDisabled);
    setFlgCancelado(false);
    setTimeout(() => {
      handleFocus('busca_parceiro');
    }, 60);
  }, [reset, handleFocus]);

  const getProductData = useCallback(
    async (product: productProps) => {
      if (!product?.cod_produto) return;

      try {
        const { data } = await romaneioServices.getProductsData(
          product.cod_produto,
          lojaSelected,
          watch('busca_parceiro').value,
        );

        if (!data || data.length === 0) return;

        const { des_unidade_compra, qtd_embalagem_compra, val_custo_rep } =
          data[0];

        setValue(
          'des_un',
          unidades.find((i) => String(i.value).trim() === des_unidade_compra),
        );
        setValue('des_emb', qtd_embalagem_compra);
        setValue('val_custo_unit', formatCurrencyAsText(val_custo_rep));
        setDisabledInputs((prev) => ({
          ...prev,
          loja: true,
          parceiro: true,
          produto: true,
        }));
        handleFocus('cod_tipo_ajuste');
      } catch (error) {
        console.error('Erro ao buscar dados do produto:', error);
      }
    },
    [lojaSelected, watch, setValue, unidades, handleFocus],
  );

  const handleProdutoAssociado = useCallback(
    async (produto: productProps) => {
      const result = await MySwal.fire({
        title: 'Confirmação',
        text: 'Não é permitido fazer Ajuste de Estoque em Item Associado. Deseja pesquisar o produto pai?',
        showCancelButton: true,
        showCloseButton: true,
        confirmButtonColor: '#28A745',
        cancelButtonColor: '#DC3545',
        confirmButtonText: 'Sim',
        cancelButtonText: 'Não',
        icon: 'question',
      });
      if (result.isConfirmed) {
        if (!produto.cod_associado || !produto.cod_loja) return;

        const prodPai: any = await romaneioServices.getProductPai(
          produto.cod_associado,
          produto.cod_loja,
        );

        if (prodPai) {
          const formattedQuantity = moneyFormat(
            prodPai.qtd_est_atual.toString(),
            3,
            3,
          );
          const formattedPrice = !prodPai.val_preco
            ? '0,00'
            : moneyFormat(prodPai.val_preco.toString(), 2, 2);

          setValue('busca_produto', {
            value: prodPai.cod_produto,
            label: `${prodPai.cod_produto} - ${prodPai.des_produto} | Lj: ${prodPai.cod_loja} | Venda: R$ ${formattedPrice} | Estq.: ${formattedQuantity} |`,
          });
          setProductSelected(prodPai);
          await getProductData(prodPai);
          return;
        }
      }
      setTimeout(() => {
        handleFocus('busca_produto');
      }, 300);
    },
    [handleFocus, setValue, getProductData],
  );

  const handleProductSelected = useCallback(
    async (product: productProps) => {
      const isAssociado =
        product.cod_associado !== null &&
        typeof product.cod_associado === 'string';

      if (isAssociado) {
        await handleProdutoAssociado(product);
        return;
      }

      await getProductData(product);
      setProductSelected(product);
    },
    [handleProdutoAssociado, getProductData],
  );

  const onCancelProduct = useCallback(() => {
    setValue('busca_produto', null);
    setValue('qtd_ajuste', null);
    setValue('num_docto', null);
    setValue('des_un', null);
    setValue('des_emb', null);
    setValue('val_custo_unit', null);
    setProductSelected(productDefault);
    setDisabledInputs((prev) => ({
      ...prev,
      loja: false,
      parceiro: false,
      produto: false,
    }));
    handleFocus('product');
  }, [setValue, handleFocus]);

  const onRowClick = useCallback(
    async (param: GridRowParams) => {
      const { row } = param;
      const {
        cod_seq_ajuste_estoque,
        cod_produto,
        cod_loja,
        tipo_operacao,
        tipo_operacao_view,
        cod_tipo_ajuste,
        qtd_ajuste,
        num_documento,
        qtd_estoque_ant,
        flg_cancelado,
        des_produto,
        val_custo_rep,
        des_unidade,
        qtd_embalagem,
        flg_ipv,
      } = row;
      setShowSearch(false);
      setUpdate(true);
      setValue('cod_seq_ajuste_estoque', cod_seq_ajuste_estoque);
      setValue('cod_tipo_ajuste', {
        value: tipo_operacao,
        label: `${cod_tipo_ajuste} - ${tipo_operacao_view}`,
      });
      setValue('num_docto', num_documento);
      setValue('qtd_ajuste', qtd_ajuste);
      setValue(
        'des_un',
        unidades.find((i) => String(i.value)?.trim() === des_unidade?.trim()),
      );
      setValue('des_emb', qtd_embalagem);
      setValue('val_custo_unit', formatCurrencyAsText(val_custo_rep));
      setLojaSelected(cod_loja);
      setProductSelected({
        ...productDefault,
        cod_produto,
        des_produto,
        qtd_total_estoque: qtd_estoque_ant,
        val_preco: val_custo_rep,
        cod_loja,
        des_unidade_compra: des_unidade,
        flg_ipv,
      });
      setFlgCancelado(flg_cancelado);
      setDisabledInputs({
        loja: true,
        parceiro: true,
        produto: true,
        ajuste: true,
        qtd: true,
        un: true,
        emb: true,
        val_custo: true,
        docto: true,
      });
    },
    [unidades, setValue],
  );

  const onSave = handleSubmit(async (data) => {
    const estoque =
      productSelected.qtd_total_estoque ?? productSelected.qtd_est_atual ?? 0;

    if (!productSelected?.cod_produto || estoque === null) {
      return toast.warn('Há informações pendentes');
    }

    const qtdAjuste = transformAsCurrency(data.qtd_ajuste);
    const qtdEmbalagem = transformAsCurrency(data.des_emb);
    const qtdEstoqueAnt = transformAsCurrency(estoque);
    const val_custo_rep = transformAsCurrency(data.val_custo_unit ?? 0);

    if (val_custo_rep <= 0) {
      setError('val_custo_unit', { type: 'required' });
      setTimeout(() => {
        setFocus('val_custo_unit', { shouldSelect: true });
      }, 60);
      return toast.warn('Custo unitário deve ser maior que 0');
    }

    const tipoUnidadeAjuste = data.cod_tipo_ajuste.tipo_unidade ?? 0;
    const operacao =
      data.cod_tipo_ajuste.tipo_operacao === 1 ? 'subtracao' : 'soma';

    const qtdEstoquePost = calculaQtdEstPost(
      operacao,
      estoque,
      qtdEmbalagem,
      qtdAjuste,
      tipoUnidadeAjuste,
    );

    const formData = {
      cod_loja: lojaSelected,
      cod_fornecedor: data.busca_parceiro.value,
      cod_produto: productSelected.cod_produto,
      cod_tipo_ajuste: data.cod_tipo_ajuste.value,
      qtd_ajuste: qtdAjuste,
      qtd_estoque_ant: qtdEstoqueAnt,
      qtd_estoque_post: qtdEstoquePost,
      des_unidade: data.des_un.value,
      qtd_embalagem: qtdEmbalagem,
      val_custo_rep,
      num_documento: data.num_docto,
    };

    if (loja.regra_empresa.per_bloq_custo > 0) {
      const tolera = toleraValor(
        val_custo_rep,
        productSelected.val_custo_rep ?? 0,
        loja.regra_empresa.per_bloq_custo,
      );

      if (!tolera) {
        await MySwal.fire({
          title: 'Custo excedeu o percentual de bloqueio',
          text: 'Regularize a situação para prosseguir',
          icon: 'error',
        });
        return;
      }
    }

    if (loja.regra_empresa.per_tol_custo > 0) {
      const tolera = toleraValor(
        val_custo_rep,
        productSelected.val_custo_rep ?? 0,
        loja.regra_empresa.per_tol_custo,
      );

      if (!tolera) {
        await MySwal.fire({
          title: 'Custo excedeu o percentual de tolerância',
          icon: 'warning',
          confirmButtonText: 'Continuar',
        });
      }
    }

    try {
      const res = await romaneioServices.gravarAjuste(formData);
      if (res.success) {
        setShowSearch(false);
        resetFormData();
        toast.success(res.message);
      }
    } catch (error: any) {
      toast.warn(error.message);
    }
  });

  const handleCancel = useCallback(async () => {
    if (flgCancelado) return toast.warn('Ajuste já cancelado');
    const { success, message } = await romaneioServices.cancelarAjuste(
      watch('cod_seq_ajuste_estoque'),
    );
    if (success) {
      toast.success(message);
      setShowSearch(true);
      resetFormData();
    }
  }, [flgCancelado, resetFormData, watch]);

  const onNew = useCallback(() => {
    resetFormData();
    setUpdate(false);
    setShowSearch(false);
    refetchTiposAjuste();
  }, [resetFormData, refetchTiposAjuste]);

  const value: RomaneioFornContextData = useMemo(
    () => ({
      formTela,
      showSearch,
      setShowSearch,
      isUpdate,
      setUpdate,
      productSelected,
      lojaSelected,
      flgCancelado,
      tiposAjuste,
      disabledInputs,
      setDisabledInputs,
      unidades,

      resetFormData,
      onRowClick,
      onSave,
      handleCancel,
      onNew,
      handleProductSelected,
      onCancelProduct,
      handleLojaSelected,
      handleFocus,
      refetchTiposAjuste,
      refetchUnidades,
    }),
    [
      formTela,
      showSearch,
      isUpdate,
      productSelected,
      lojaSelected,
      flgCancelado,
      tiposAjuste,
      disabledInputs,
      unidades,

      resetFormData,
      onRowClick,
      onSave,
      handleCancel,
      onNew,
      handleProductSelected,
      onCancelProduct,
      handleLojaSelected,
      handleFocus,
      refetchTiposAjuste,
      refetchUnidades,
    ],
  );
  return (
    <RomaneioFornContext.Provider value={value}>
      {children}
    </RomaneioFornContext.Provider>
  );
};
